import React, { useState, useEffect } from 'react';
import { usersApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Plus, UserCircle } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-purple-100 text-purple-800', desc: 'Platform owner - hospital account management only' },
  { value: 'hospital_admin', label: 'Hospital Admin', color: 'bg-blue-100 text-blue-800', desc: 'Full hospital access' },
  { value: 'claim_manager', label: 'Claim Manager', color: 'bg-green-100 text-green-800', desc: 'Claims, Pre-auth, Denials, AR' },
  { value: 'billing_executive', label: 'Billing Executive', color: 'bg-orange-100 text-orange-800', desc: 'Billing, Docs, Payments' },
  { value: 'tpa_desk', label: 'TPA Desk', color: 'bg-yellow-100 text-yellow-800', desc: 'TPA/Insurance communication' },
  { value: 'ar_executive', label: 'AR Executive', color: 'bg-pink-100 text-pink-800', desc: 'AR follow-ups & collections' },
  { value: 'finance_manager', label: 'Finance Manager', color: 'bg-indigo-100 text-indigo-800', desc: 'Payments, Reports, Dashboard' },
  { value: 'management_viewer', label: 'Management Viewer', color: 'bg-teal-100 text-teal-800', desc: 'Read-only: Dashboard, Reports' },
  { value: 'reception_user', label: 'Reception User', color: 'bg-cyan-100 text-cyan-800', desc: 'Patient registration, basic claims' },
];

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'billing_executive'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await usersApi.list();
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await usersApi.create(formData);
      toast.success('User created successfully!');
      setDialogOpen(false);
      setFormData({ email: '', password: '', name: '', role: 'billing_executive' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLES.find(r => r.value === role);
    return roleConfig || { label: role, color: 'bg-gray-100 text-gray-800' };
  };

  // Filter available roles based on current user
  const availableRoles = currentUser?.role === 'super_admin' 
    ? ROLES 
    : ROLES.filter(r => !['super_admin', 'hospital_admin'].includes(r.value));

  return (
    <div className="space-y-6" data-testid="users-page">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light font-['Outfit'] text-[#1C1C1C]">Users</h1>
          <p className="text-[#595959] mt-1">Manage system users and their roles</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white"
              data-testid="add-user-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Add New User</DialogTitle>
              <DialogDescription>
                Create a new user account
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="User's full name"
                  required
                  data-testid="user-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="user@hospital.com"
                  required
                  data-testid="user-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="user-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={formData.role} onValueChange={(v) => handleChange('role', v)}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex flex-col">
                          <span>{role.label}</span>
                          <span className="text-[10px] text-slate-400">{role.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#2D4A3E] hover:bg-[#1F362C] text-white" data-testid="save-user-btn">
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D4A3E]"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-6">
              <UserCircle className="h-16 w-16 text-[#A3A3A3] mb-4" weight="light" />
              <p className="text-lg font-medium text-[#1C1C1C]">No users yet</p>
              <p className="text-sm text-[#595959] mt-1">Add your first user to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F5F0] hover:bg-[#F5F5F0]">
                    <TableHead className="font-semibold text-[#1C1C1C]">Name</TableHead>
                    <TableHead className="font-semibold text-[#1C1C1C]">Email</TableHead>
                    <TableHead className="font-semibold text-[#1C1C1C]">Role</TableHead>
                    <TableHead className="font-semibold text-[#1C1C1C]">Status</TableHead>
                    <TableHead className="font-semibold text-[#1C1C1C]">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => {
                    const roleBadge = getRoleBadge(user.role);
                    return (
                      <TableRow 
                        key={user.id} 
                        className="table-row-hover"
                        data-testid={`user-row-${user.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-[#2D4A3E] flex items-center justify-center text-white text-sm font-medium">
                              {user.name?.charAt(0) || 'U'}
                            </div>
                            <span className="font-medium text-[#1C1C1C]">{user.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-[#595959]">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={roleBadge.color}>
                            {roleBadge.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={user.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {user.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[#595959]">
                          {new Date(user.created_at).toLocaleDateString('en-IN')}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersPage;
